import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'common';
import {
  isStayPartnerType, ORDER_TYPES, ORDER_STATUS, nightsBetween,
  buildStaySnapshot, buildPurchaseItemsSnapshot, priceWithFees,
} from 'common/src/other/PartnerUtils';
import {
  Box, Typography, Stack, Button, Grid, Paper, TextField, MenuItem,
  Switch, FormControlLabel, IconButton, Divider, Avatar, Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CircularLoading from '../components/CircularLoading';
import { MAIN_COLOR, FONT_FAMILY } from '../common/sharedFunctions';

export default function PartnerCheckout() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { partnerId } = useParams();
  const auth = useSelector((s) => s.auth);
  const partnerdata = useSelector((s) => s.partnerdata);
  const inventorydata = useSelector((s) => s.inventorydata);

  const [partner, setPartner] = useState(null);
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [roomItemId, setRoomItemId] = useState('');
  const [cart, setCart] = useState({});
  const [requestedRide, setRequestedRide] = useState(false);
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupLat, setPickupLat] = useState('');
  const [pickupLng, setPickupLng] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { fetchPartnerById, fetchInventory, createPartnerOrder, reserveAvailability } = api;

  useEffect(() => {
    dispatch(fetchInventory(partnerId));
    dispatch(fetchPartnerById(partnerId)).then((p) => p && setPartner(p));
  }, [dispatch, fetchPartnerById, fetchInventory, partnerId]);

  useEffect(() => {
    if (!partner && partnerdata.partners) {
      const found = partnerdata.partners.find((p) => p.id === partnerId);
      if (found) setPartner(found);
    }
  }, [partner, partnerdata.partners, partnerId]);

  const inventory = inventorydata.byPartnerId?.[partnerId] || [];
  const isStay = partner ? isStayPartnerType(partner.partnerType) : false;
  const selectedRoom = useMemo(
    () => inventory.find((i) => i.id === roomItemId),
    [inventory, roomItemId]
  );
  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return nightsBetween(new Date(checkIn).getTime(), new Date(checkOut).getTime());
  }, [checkIn, checkOut]);

  const cartLines = useMemo(() => {
    return Object.entries(cart)
      .filter(([, qty]) => qty > 0)
      .map(([itemId, qty]) => {
        const item = inventory.find((i) => i.id === itemId);
        return item ? {
          itemId, qty,
          name: item.name,
          price: Number(item.price) || 0,
          image: item.images && Object.values(item.images)[0],
        } : null;
      })
      .filter(Boolean);
  }, [cart, inventory]);

  const subtotal = useMemo(() => {
    if (isStay && selectedRoom && nights > 0) {
      return Number(selectedRoom.price || 0) * nights;
    }
    return cartLines.reduce((acc, l) => acc + l.price * l.qty, 0);
  }, [isStay, selectedRoom, nights, cartLines]);

  const totals = useMemo(() => priceWithFees({
    subtotal,
    serviceFeeRate: (Number(partner?.serviceFee) || 0) / 100,
    flatFee: Number(partner?.commissionFlat) || 0,
  }), [subtotal, partner]);

  const onCartChange = (itemId, delta) => {
    setCart((prev) => ({ ...prev, [itemId]: Math.max(0, (prev[itemId] || 0) + delta) }));
  };

  const canSubmit = () => {
    if (!auth?.profile?.uid) return false;
    if (isStay) {
      if (!checkIn || !checkOut || !roomItemId) return false;
      if (nights <= 0) return false;
    } else if (cartLines.length === 0) {
      return false;
    }
    if (requestedRide && (!pickupAddress || !pickupLat || !pickupLng)) return false;
    return true;
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const orderType = isStay ? ORDER_TYPES.STAY : ORDER_TYPES.PURCHASE;
      const orderInput = {
        customer: auth.profile.uid,
        customerName: `${auth.profile.firstName || ''} ${auth.profile.lastName || ''}`.trim(),
        customerPhone: auth.profile.mobile || '',
        partnerId,
        partnerType: partner.partnerType,
        partnerName: partner.businessName,
        partnerCover: partner.images?.cover || null,
        orderType,
        status: ORDER_STATUS.PENDING,
        zoneId: partner.zoneId || null,
        currency: partner.currency || 'USD',
        subtotal: totals.subtotal,
        tax: totals.tax,
        fees: totals.serviceFee + totals.flatFee,
        deliveryFee: totals.deliveryFee,
        discount: totals.discount,
        total: totals.total,
        payment_mode: paymentMode,
        paid: false,
        notes,
        stay: isStay ? {
          checkIn: new Date(checkIn).getTime(),
          checkOut: new Date(checkOut).getTime(),
          nights,
          guests: { adults, children },
          roomItemId,
          roomSnapshot: buildStaySnapshot(selectedRoom),
          requestedRide,
          rideAddress: requestedRide ? {
            lat: parseFloat(pickupLat),
            lng: parseFloat(pickupLng),
            add: pickupAddress,
            time: new Date(checkIn).getTime(),
          } : null,
        } : null,
        purchase: !isStay ? {
          items: buildPurchaseItemsSnapshot(cartLines),
          delivery: { type: 'pickup' },
        } : null,
      };

      if (isStay) {
        await dispatch(reserveAvailability(
          partnerId,
          roomItemId,
          new Date(checkIn).getTime(),
          new Date(checkOut).getTime(),
          1,
        ));
      }
      const orderId = await dispatch(createPartnerOrder(orderInput));
      navigate(`/myorders/${orderId}`);
    } catch (err) {
      window.alert(err?.message || 'Error creating order');
    } finally {
      setSubmitting(false);
    }
  };

  if (!partner) return <CircularLoading />;

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT_FAMILY }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate(`/partners/${partnerId}`)}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t('checkout') || 'Checkout'} — {partner.businessName}
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
            {isStay ? (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t('your_stay') || 'Tu reserva'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" type="date"
                      label={t('check_in') || 'Check-in'}
                      InputLabelProps={{ shrink: true }}
                      value={checkIn} onChange={(e) => setCheckIn(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" type="date"
                      label={t('check_out') || 'Check-out'}
                      InputLabelProps={{ shrink: true }}
                      value={checkOut} onChange={(e) => setCheckOut(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth size="small" type="number" label={t('adults') || 'Adultos'}
                      value={adults} onChange={(e) => setAdults(parseInt(e.target.value, 10) || 1)}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth size="small" type="number" label={t('children') || 'Niños'}
                      value={children} onChange={(e) => setChildren(parseInt(e.target.value, 10) || 0)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select fullWidth size="small" label={t('select_room') || 'Habitación'}
                      value={roomItemId} onChange={(e) => setRoomItemId(e.target.value)}
                    >
                      {inventory
                        .filter((i) => i.active !== false)
                        .map((i) => (
                          <MenuItem key={i.id} value={i.id}>
                            {i.name} — {partner.currency} {Number(i.price || 0).toFixed(2)}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />
                <FormControlLabel
                  control={
                    <Switch
                      checked={requestedRide}
                      onChange={(e) => setRequestedRide(e.target.checked)}
                    />
                  }
                  label={t('add_ride_to_hotel') || 'Necesito que me lleven al hotel'}
                />
                {requestedRide && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth size="small"
                        label={t('pickup_address') || 'Dirección de recogida'}
                        value={pickupAddress} onChange={(e) => setPickupAddress(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small" type="number" label="Lat"
                        value={pickupLat} onChange={(e) => setPickupLat(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth size="small" type="number" label="Lng"
                        value={pickupLng} onChange={(e) => setPickupLng(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {t('your_cart') || 'Tu carrito'}
                </Typography>
                <Stack spacing={1.25}>
                  {inventory.filter((i) => i.active !== false).map((item) => {
                    const qty = cart[item.id] || 0;
                    return (
                      <Paper key={item.id} variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Avatar variant="rounded"
                            src={item.images && Object.values(item.images)[0]}
                            sx={{ width: 56, height: 56 }}
                          >
                            {(item.name || '?').slice(0, 1)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 600 }}>{item.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {partner.currency} {Number(item.price || 0).toFixed(2)}
                            </Typography>
                          </Box>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <IconButton size="small" onClick={() => onCartChange(item.id, -1)} disabled={!qty}>
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ minWidth: 24, textAlign: 'center' }}>{qty}</Typography>
                            <IconButton size="small" onClick={() => onCartChange(item.id, 1)}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              </>
            )}
            <Divider sx={{ my: 3 }} />
            <TextField
              fullWidth size="small" multiline rows={2}
              label={t('notes') || 'Notas'}
              value={notes} onChange={(e) => setNotes(e.target.value)}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, position: 'sticky', top: 16 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {t('order_summary') || 'Resumen'}
            </Typography>
            {isStay ? (
              <Stack direction="row" justifyContent="space-between">
                <Typography>{nights} {t('nights') || 'noches'}</Typography>
                <Typography>{partner.currency} {totals.subtotal.toFixed(2)}</Typography>
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="space-between">
                <Typography>
                  {cartLines.reduce((a, l) => a + l.qty, 0)} {t('items') || 'items'}
                </Typography>
                <Typography>{partner.currency} {totals.subtotal.toFixed(2)}</Typography>
              </Stack>
            )}
            {totals.serviceFee + totals.flatFee > 0 && (
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                <Typography color="text.secondary">{t('fees') || 'Cargos'}</Typography>
                <Typography>
                  {partner.currency} {(totals.serviceFee + totals.flatFee).toFixed(2)}
                </Typography>
              </Stack>
            )}
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" justifyContent="space-between">
              <Typography sx={{ fontWeight: 700 }}>{t('total') || 'Total'}</Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {partner.currency} {totals.total.toFixed(2)}
              </Typography>
            </Stack>

            <TextField
              select fullWidth size="small" sx={{ mt: 2 }}
              label={t('payment_mode') || 'Pago'}
              value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}
            >
              <MenuItem value="cash">{t('cash') || 'Efectivo'}</MenuItem>
              <MenuItem value="wallet">{t('wallet') || 'Wallet'}</MenuItem>
              <MenuItem value="card">{t('card') || 'Tarjeta'}</MenuItem>
            </TextField>

            <Button
              fullWidth variant="contained" size="large"
              sx={{ mt: 2, backgroundColor: MAIN_COLOR, borderRadius: 999 }}
              disabled={!canSubmit() || submitting}
              onClick={onSubmit}
            >
              {submitting ? '...' : (t('confirm_booking') || 'Confirmar reserva')}
            </Button>
            {requestedRide && (
              <Chip
                size="small"
                label={t('ride_addon_chip') || 'Incluye traslado al hotel'}
                sx={{ mt: 1.5, backgroundColor: '#FFF7ED', color: MAIN_COLOR, fontWeight: 600 }}
              />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
